import {useRef,useState,type PointerEvent} from 'react';
/** October 28 owner screenshots (155556 through 155630):
 * seven-day FloatHelm Availability Overrides grid, green recurring Flexible
 * Booking blocks with explicit booking duration and increment. Read-only until
 * owner-reviewed editing operations are implemented; never imply a guest booking.
 */
export type FlexibleRoomWindow={
  weekday:number;opensMinute:number;closesMinute:number;
  flexibleBookingMinutes:number|null;incrementMinutes:number|null;
};
const weekdays=[
  {day:1,label:'Mon'},{day:2,label:'Tue'},{day:3,label:'Wed'},
  {day:4,label:'Thu'},{day:5,label:'Fri'},{day:6,label:'Sat'},{day:0,label:'Sun'},
];
const pixelsPerMinute=0.85;
const clock=(value:number)=>{
  const h=Math.floor(value/60),m=value%60;
  return (h%12||12)+':'+String(m).padStart(2,'0')+(h<12?'am':'pm');
};
/** Side-by-side layout for intentional overlapping flexible blocks.
 * Physical booking availability is their UNION, while these rows preserve
 * the individually editable Helm windows and their own increments. */
function windowPlacement(window:{weekday:number;opensMinute:number;closesMinute:number},
  all:{weekday:number;opensMinute:number;closesMinute:number}[]){
  const sameDay=all.filter(w=>w.weekday===window.weekday)
    .sort((a,b)=>a.opensMinute-b.opensMinute||a.closesMinute-b.closesMinute);
  if(!sameDay.some(w=>w!==window&&w.opensMinute<window.closesMinute&&
    window.opensMinute<w.closesMinute))return {left:'3px',right:'3px'};
  const laneEnds:number[]=[];
  let chosen=0;
  for(const w of sameDay){
    let lane=laneEnds.findIndex(end=>end<=w.opensMinute);
    if(lane<0){lane=laneEnds.length;laneEnds.push(0);}
    laneEnds[lane]=w.closesMinute;
    if(w===window)chosen=lane;
  }
  const portion=100/laneEnds.length;
  return {left:`${portion*chosen+1}%`,
    right:`${portion*(laneEnds.length-chosen-1)+1}%`};
}

export function RoomFlexibleAvailabilityWeek({roomName,windows,
  onSelect,onCreate}:{roomName:string;windows:FlexibleRoomWindow[];
  onSelect?:(window:FlexibleRoomWindow,original:boolean)=>void;
  onCreate?:(weekday:number,opensMinute:number,closesMinute:number)=>void;
}){
  const dragRef=useRef<{weekday:number;start:number}|null>(null);
  const [dragPreview,setDragPreview]=useState<{weekday:number;start:number;end:number}|null>(null);
  const display=windows;
  const start=Math.min(9*60,...display.map(window=>Math.floor(window.opensMinute/60)*60));
  const end=Math.max(22*60,...display.map(window=>Math.ceil(window.closesMinute/60)*60));
  return <section className="ecfs-flexible-week" aria-label={'Flexible Booking availability for '+roomName}>
    <p className="ecfs-flexible-week-caption">
      Click or drag within a day to add availability, or select a block to edit its start, end and booking increment.
    </p>
    {!display.length&&<p role="status">No availability configured. Click a day to add a time block.</p>}
    {<div className="ecfs-flexible-week-scroll">
      <div className="ecfs-flexible-week-grid">
        <div className="ecfs-flexible-week-corner"/>
        {weekdays.map(day=><strong className="ecfs-flexible-week-day" key={day.day}>{day.label}</strong>)}
        <div className="ecfs-flexible-week-axis" style={{height:(end-start)*pixelsPerMinute}}>
          {Array.from({length:Math.floor((end-start)/60)+1},(_,i)=>start+i*60).map(min=><span key={min}
            style={{top:(min-start)*pixelsPerMinute}}>{clock(min)}</span>)}
        </div>
        {weekdays.map(day=><div key={day.day}
          role="group" aria-label={day.label+' recurring room availability'}
          className="ecfs-flexible-week-column"
          style={{height:(end-start)*pixelsPerMinute}}
          onPointerDown={(e:PointerEvent<HTMLDivElement>)=>{
             if(!onCreate||e.target!==e.currentTarget)return;
             const rect=e.currentTarget.getBoundingClientRect();
             const minute=start+Math.floor((e.clientY-rect.top)/pixelsPerMinute/15)*15;
             const at=Math.max(0,Math.min(1425,minute));
             dragRef.current={weekday:day.day,start:at};
             setDragPreview({weekday:day.day,start:at,end:at+15});
             e.currentTarget.setPointerCapture(e.pointerId);
             e.preventDefault();
           }}
           onPointerMove={e=>{
             const dragging=dragRef.current;
             if(!dragging||dragging.weekday!==day.day)return;
             const rect=e.currentTarget.getBoundingClientRect();
             const minute=start+Math.floor((e.clientY-rect.top)/pixelsPerMinute/15)*15;
             const snapped=Math.max(0,Math.min(1425,minute));
             setDragPreview({weekday:day.day,start:Math.min(dragging.start,snapped),
               end:Math.max(dragging.start+15,snapped+15)});
           }}
           onPointerUp={e=>{
             const dragging=dragRef.current;
             if(!dragging||dragging.weekday!==day.day)return;
             const rect=e.currentTarget.getBoundingClientRect();
             const minute=start+Math.floor((e.clientY-rect.top)/pixelsPerMinute/15)*15;
             const snapped=Math.max(0,Math.min(1425,minute));
             onCreate?.(day.day,Math.min(dragging.start,snapped),
               Math.max(dragging.start+15,snapped+15));
             dragRef.current=null;setDragPreview(null);
             if(e.currentTarget.hasPointerCapture(e.pointerId))
               e.currentTarget.releasePointerCapture(e.pointerId);
           }}
           onPointerCancel={()=>{dragRef.current=null;setDragPreview(null);}}>
           {dragPreview?.weekday===day.day&&
             <div className="ecfs-flexible-week-preview" style={{
               top:(dragPreview.start-start)*pixelsPerMinute,
               height:(dragPreview.end-dragPreview.start)*pixelsPerMinute,
             }} aria-hidden="true">
               {clock(dragPreview.start)} - {clock(dragPreview.end)}
             </div>}
           {display.filter(w=>w.weekday===day.day).map(w=>
            <button type="button" className="ecfs-flexible-week-window"
              key={w.opensMinute+'-'+w.closesMinute}
              style={{...windowPlacement(w,display),
                top:(w.opensMinute-start)*pixelsPerMinute,
                height:(w.closesMinute-w.opensMinute)*pixelsPerMinute}}
              onClick={e=>{e.stopPropagation();
                if(onSelect)onSelect({
                  weekday:w.weekday,opensMinute:w.opensMinute,closesMinute:w.closesMinute,
                  flexibleBookingMinutes:w.flexibleBookingMinutes,
                  incrementMinutes:w.incrementMinutes,
                },true);}}
              aria-label={clock(w.opensMinute)+' to '+clock(w.closesMinute)+
                (w.incrementMinutes!==null?
                  ', Flexible Booking ('+w.incrementMinutes+'m)':', Not flexible')}>
              <strong>{clock(w.opensMinute)} - {clock(w.closesMinute)}</strong>
              <span>{w.incrementMinutes!==null?
                'Flexible Booking ('+w.incrementMinutes+'m)':'Not Flexible Booking'}</span>
            </button>)}
        </div>)}
      </div>
    </div>}
  </section>;
}
